import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import MeasuredItem from './components/MeasuredItem';
import { useRafBatcher } from './hooks/useRafBatcher';
import { useMasonryLayout } from './hooks/useMasonryLayout';
import { useScrollViewport } from './hooks/useScrollViewport';
import { useVirtualItems } from './hooks/useVirtualItems';

const VirtualMasonryGrid = forwardRef(function VirtualMasonryGrid(
  {
    items,
    columns,
    gap = 12,
    className,
    style,
    renderItem,
    getKey,
    estimateHeight,
    defaultEstimate = 240,

    measureEpsilonPx = 4,

    overscanPx = 1200,
    enablePinOnFocus = true,
  },
  ref,
) {
  const keyOf = useMemo(() => getKey || ((item) => String(item.id)), [getKey]);

  const scrollRef = useRef(null);

  const heightsRef = useRef(new Map());
  const [layoutVersion, setLayoutVersion] = useState(0);

  const { schedule, beginBatch, endBatch } = useRafBatcher();

  const requestRelayout = useCallback(() => {
    schedule(() => setLayoutVersion((v) => v + 1));
  }, [schedule]);

  const onHeight = useCallback(
    (id, nextHeight) => {
      const h = Number.isFinite(nextHeight) ? Math.max(0, nextHeight) : 0;
      const prev = heightsRef.current.get(id);

      if (prev == null || Math.abs(prev - h) >= measureEpsilonPx) {
        heightsRef.current.set(id, h);
        requestRelayout();
      }
    },
    [measureEpsilonPx, requestRelayout],
  );

  const pinnedIdsRef = useRef(new Set());
  const [pinVersion, setPinVersion] = useState(0);

  const onFocusWithinChange = useCallback(
    (id, isFocused) => {
      if (!enablePinOnFocus) return;

      const set = pinnedIdsRef.current;
      const had = set.has(id);

      if (isFocused && !had) {
        set.add(id);
        setPinVersion((v) => v + 1);
      } else if (!isFocused && had) {
        set.delete(id);
        setPinVersion((v) => v + 1);
      }
    },
    [enablePinOnFocus],
  );

  const layout = useMasonryLayout({
    items,
    columns,
    gap,
    keyOf,
    heightsById: heightsRef.current,
    estimateHeight,
    defaultEstimate,
    layoutVersion,
  });

  const { scrollTop, viewportHeight } = useScrollViewport(scrollRef);

  const visibleIds = useVirtualItems({
    positions: layout.positions,
    scrollTop,
    viewportHeight,
    overscanPx,
    pinnedIdsSet: pinnedIdsRef.current,
  });

  const _pinVersion = pinVersion;

  useImperativeHandle(
    ref,
    () => ({
      beginLayoutBatch: beginBatch,
      endLayoutBatch: endBatch,
      scrollToTop: () => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      },
      getHeightsSnapshot: () => new Map(heightsRef.current),
      getPinnedIds: () => new Set(pinnedIdsRef.current),
    }),
    [beginBatch, endBatch],
  );

  return (
    <div
      ref={scrollRef}
      className={className}
      style={{
        position: 'relative',
        overflow: 'auto',
        ...style,
      }}>
      <div style={{ position: 'relative', height: layout.contentHeight }}>
        {layout.positions.map(({ id, item, top, leftExpr, widthExpr }) => {
          if (!visibleIds.has(id)) return null;

          return (
            <div
              key={id}
              style={{
                position: 'absolute',
                left: leftExpr,
                top,
                width: widthExpr,
                boxSizing: 'border-box',
              }}>
              <MeasuredItem
                id={id}
                onHeight={onHeight}
                epsilonPx={measureEpsilonPx}
                onFocusWithinChange={enablePinOnFocus ? onFocusWithinChange : undefined}>
                {renderItem(item)}
              </MeasuredItem>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default VirtualMasonryGrid;
